import { useEffect } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { autoSubscribeConnector } from "@/components/portal/connectors/ConnectorCreateForm";
import { useAuth } from "@/contexts/AuthContext";
import { formatTypeName } from "@/lib/connectorTypes";

/**
 * Landing route for connector OAuth callbacks redirected back from
 * business2cloud (`AuthoriseController.CallbackAsync`).
 *
 * B2C's success path:
 *   {portalUrl}/connectors/{provider}/manage?organisation={orgId}#{tenantId}
 *
 * B2C's error paths append `&error=...` (e.g. `unauthorised` or a domain
 * exception message). We surface those as toasts before navigating to the
 * provider's connector list page so the user sees their new connection.
 *
 * Auto-subscribes the org to the connector service on success — restores
 * the behaviour stripped in #283 when this route replaced the in-form
 * popup/postMessage flow. Subscription is fire-and-forget so the redirect
 * isn't delayed by the platform-API round-trip.
 */
export const Route = createFileRoute("/portal/connectors/$typeSlug/manage")({
  component: ConnectorManageLanding,
});

export function ConnectorManageLanding() {
  const { typeSlug } = useParams({ from: "/portal/connectors/$typeSlug/manage" });
  const navigate = useNavigate();
  const { token, isLoading } = useAuth();

  useEffect(() => {
    // Wait for AuthContext to finish bootstrapping before acting. On a
    // cold load / refresh / deep-link, useAuth reports isLoading=true
    // briefly; if we navigated in that window the user would never get
    // subscribed (the component unmounts on navigate, so we don't get a
    // second chance).
    if (isLoading) return;

    const params = new URLSearchParams(window.location.search);
    const organisation = params.get("organisation");
    const error = params.get("error");

    // B2C always appends `?organisation=` on a real callback. Treat its
    // absence as direct navigation (refresh, manual URL, stale tab) and
    // bounce silently — don't claim a success that never happened.
    //
    // The `#{tenantId}` hash B2C sets on success is intentionally ignored;
    // the connector list page doesn't deep-link per tenant.
    if (organisation) {
      const providerLabel = formatTypeName(typeSlug);
      if (error) {
        toast.error(
          error === "unauthorised"
            ? `${providerLabel} authorisation was not granted`
            : `${providerLabel} authorisation failed: ${error}`,
        );
      } else {
        // Restore the auto-subscribe stripped in #283. Fire-and-forget so
        // the redirect isn't gated on platform-API latency. Org-level
        // subscription only — the redirect exposes the connector GUID Key
        // in the URL hash but autoSubscribeConnector wants the int Id for
        // per-instance billing; resolving GUID->int Id is a follow-up.
        // The outer `.catch` is defence-in-depth — autoSubscribeConnector
        // already swallows its own errors, but we don't want an unhandled
        // promise rejection if it ever throws beyond its internal try.
        if (token) {
          autoSubscribeConnector(token, organisation, typeSlug).catch(() => {});
        }
        toast.success(`${providerLabel} connector created`);
      }
    }

    navigate({
      to: "/portal/connectors/$typeSlug",
      params: { typeSlug },
      replace: true,
    });
  }, [typeSlug, navigate, token, isLoading]);

  return (
    <div className="flex items-center justify-center py-16 text-sm text-slate-500">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      Finalising {formatTypeName(typeSlug)} connection…
    </div>
  );
}
